import { PageHeaderWrapper, Text, Title, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type Theme } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { IconButton } from "../../../components/IconButton";
import { PageHeaderTopRow } from "../../../components/PageHeaderTopRow";
import { PayRowLabel } from "../../../Pay/PayRowLabel";
import { ShiftQualificationLabel } from "../../../Shift/QualificationLabel";
import { SHIFT_HISTORY_FULL_PATH } from "../../path";
import { type MissedPunchRequestStatus } from "../types";
import { PastShiftOptionsBottomSheet } from "./PastShiftOptionsBottomSheet";
import { type PastShiftPillStatus, PastShiftStatusPill } from "./PastShiftStatusPill";

const ERROR_STATUSES = new Set<MissedPunchRequestStatus>(["declined", "expired"]);

interface PastShiftDetailsHeaderProps {
  shiftId: string;
  dateLabel: string;
  qualificationName: string;
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isVerified: boolean;
  totalPayInDollars: number;
  hourlyRateInDollars: number;
  requestedAmountInDollars?: number;
  isFullShiftPayment: boolean;
}

function getBackgroundGradient(
  theme: Theme,
  missedPunchRequestStatus: MissedPunchRequestStatus,
  isVerified: boolean
): string | undefined {
  if (missedPunchRequestStatus === "pending") {
    return theme.palette.shift?.inreview?.gradient;
  }

  if (ERROR_STATUSES.has(missedPunchRequestStatus) || !isVerified) {
    return theme.palette.shift?.unverified?.gradient;
  }

  return theme.palette.shift?.verified?.gradient;
}

function getPillStatus(
  missedPunchRequestStatus: MissedPunchRequestStatus,
  isUnverifiedWithoutMissedPunchRequest: boolean
): PastShiftPillStatus {
  if (isUnverifiedWithoutMissedPunchRequest) {
    return "unverified";
  }

  return missedPunchRequestStatus;
}

function getPayTextColor(
  theme: Theme,
  missedPunchRequestStatus: MissedPunchRequestStatus
): string | undefined {
  if (missedPunchRequestStatus === "pending") {
    return theme.palette.intent?.warning.text;
  }

  if (ERROR_STATUSES.has(missedPunchRequestStatus)) {
    return theme.palette.intent?.error.text;
  }

  return undefined;
}

function formatAdjustmentAmount(amountInDollars: number): string {
  const formattedAmount = formatDollarsAsUsd(Math.abs(amountInDollars));
  return amountInDollars >= 0 ? `(+${formattedAmount})` : `(-${formattedAmount})`;
}

export function PastShiftDetailsHeader(props: PastShiftDetailsHeaderProps) {
  const {
    shiftId,
    dateLabel,
    qualificationName,
    missedPunchRequestStatus,
    isVerified,
    totalPayInDollars,
    hourlyRateInDollars,
    requestedAmountInDollars,
    isFullShiftPayment,
  } = props;

  const optionsModalState = useModalState();

  const isUnverifiedWithoutMissedPunchRequest = !isVerified && missedPunchRequestStatus === "none";
  const showTotalPay = isVerified || missedPunchRequestStatus !== "none";
  const pillStatus = getPillStatus(missedPunchRequestStatus, isUnverifiedWithoutMissedPunchRequest);

  return (
    <>
      <PageHeaderWrapper
        sx={(theme) => ({
          background: getBackgroundGradient(theme, missedPunchRequestStatus, isVerified),
        })}
      >
        <PageHeaderTopRow
          fallbackGoBackPath={SHIFT_HISTORY_FULL_PATH}
          rightAction={
            <IconButton
              iconType="ellipsis"
              variant="outlined"
              aria-label="Options"
              onClick={() => {
                optionsModalState.openModal();
              }}
            />
          }
          sx={{ paddingX: 6 }}
        />

        <Stack spacing={3} sx={{ paddingX: 6, paddingBottom: 7 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ShiftQualificationLabel qualificationText={qualificationName} variant="body1" />
            <PastShiftStatusPill status={pillStatus} />
          </Stack>

          <Title component="h3" variant="h3">
            {dateLabel}
          </Title>

          {showTotalPay ? (
            <PayRowLabel
              primaryAmount={formatDollarsAsUsd(
                isFullShiftPayment &&
                  isDefined(requestedAmountInDollars) &&
                  requestedAmountInDollars > 0
                  ? requestedAmountInDollars
                  : totalPayInDollars
              )}
              hourlyPay={formatDollarsAsUsd(hourlyRateInDollars)}
              adjustmentAmount={
                isDefined(requestedAmountInDollars) &&
                requestedAmountInDollars > 0 &&
                !isFullShiftPayment
                  ? formatAdjustmentAmount(requestedAmountInDollars)
                  : undefined
              }
              primaryAmountColor={
                isFullShiftPayment
                  ? (theme) => getPayTextColor(theme, missedPunchRequestStatus)
                  : undefined
              }
              adjustmentAmountColor={
                isFullShiftPayment
                  ? undefined
                  : (theme) => getPayTextColor(theme, missedPunchRequestStatus)
              }
            />
          ) : (
            <Stack direction="row" justifyContent="flex-end">
              <Text semibold variant="h5">
                {`${formatDollarsAsUsd(hourlyRateInDollars)}/hr`}
              </Text>
            </Stack>
          )}
        </Stack>
      </PageHeaderWrapper>

      <PastShiftOptionsBottomSheet modalState={optionsModalState} shiftId={shiftId} />
    </>
  );
}
